import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Search, UserPlus, LogIn, LayoutDashboard,
  ChevronDown, Search as SeoIcon, Megaphone, Code2, Palette,
  Globe, Smartphone, ShoppingCart, Cloud, ShieldCheck, BookOpen,
  ArrowRight, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const { user } = useAuth();
  const servicesTimeout = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 20);

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setIsVisible(false); // Hide on scroll down
        setIsServicesOpen(false); // Close dropdowns
      } else {
        setIsVisible(true); // Show on scroll up
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  const handleServicesEnter = () => {
    clearTimeout(servicesTimeout.current);
    setIsServicesOpen(true);
  };
  const handleServicesLeave = () => {
    servicesTimeout.current = setTimeout(() => setIsServicesOpen(false), 150);
  };

  const services = [
    { name: 'SEO', path: '/services/seo', icon: SeoIcon, desc: 'Rank higher, grow faster', color: 'text-emerald-400' },
    { name: 'Digital Marketing', path: '/services/digital-marketing', icon: Megaphone, desc: 'Growth & brand awareness', color: 'text-blue-400' },
    { name: 'Custom Software', path: '/services/custom-software', icon: Code2, desc: 'Tailored business solutions', color: 'text-sky-400' },
    { name: 'UI/UX Design', path: '/services/ui-ux-design', icon: Palette, desc: 'User-centric experiences', color: 'text-cyan-400' },
    { name: 'Website Development', path: '/services/website-development', icon: Globe, desc: 'Modern & scalable websites', color: 'text-blue-400' },
    { name: 'Android Development', path: '/services/android-development', icon: Smartphone, desc: 'Native mobile applications', color: 'text-sky-400' },
    { name: 'E-commerce', path: '/services/ecommerce', icon: ShoppingCart, desc: 'Digital commerce solutions', color: 'text-blue-500' },
    { name: 'Cloud Solutions', path: '/services/cloud-solutions', icon: Cloud, desc: 'Scalable infrastructure', color: 'text-sky-400' },
    { name: 'Cyber Security', path: '/services/cyber-security', icon: ShieldCheck, desc: 'Advanced data protection', color: 'text-blue-600' },
    { name: 'Coding Courses', path: '/services/coding-courses', icon: BookOpen, desc: 'Professional skill training', color: 'text-violet-400' },
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Products', path: '/products' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="fixed top-0 left-0 w-full z-[100] pt-2 md:pt-4"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <nav className="bg-white border border-black/8 shadow-xl shadow-black/10 rounded-2xl md:rounded-[2rem] transition-all duration-300">

          <div className="flex items-center justify-between px-2.5 md:px-8 py-1.5 md:py-2.5">

            {/* ── LOGO ── */}
            <Link to="/" className="flex items-center gap-2.5 group shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 rounded-lg blur-md group-hover:bg-blue-500/40 transition-all duration-300" />
                <img
                  src="/images/logo-meharetech.png"
                  alt="MehareTech"
                  className="relative w-6 h-6 md:w-7 md:h-7 object-contain transform group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <span className="text-[13px] md:text-base font-black tracking-tight">
                <span className={`${scrolled ? 'text-blue-600' : 'text-blue-500'}`}>Mehare</span>
                <span className="bg-gradient-to-r from-blue-700 via-blue-500 to-sky-400 bg-clip-text text-transparent">Tech</span>
              </span>
            </Link>

            {/* ── DESKTOP NAV ── */}
            <div className="hidden md:flex items-center gap-1.5 lg:gap-3">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && handleServicesEnter()}
                  onMouseLeave={() => link.hasDropdown && handleServicesLeave()}
                >
                  <Link
                    to={link.path}
                    className={`relative flex items-center gap-1 px-2.5 lg:px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all duration-200 group ${isActive(link.path)
                      ? 'text-blue-600 bg-blue-50 border border-blue-200'
                      : 'text-zinc-600 hover:text-blue-600 hover:bg-blue-50/80'
                      }`}
                  >
                    {/* Active dot */}
                    {isActive(link.path) && (
                      <span className="w-1 h-1 rounded-full bg-blue-500 mr-0.5" />
                    )}
                    {link.name}
                    {link.hasDropdown && (
                      <ChevronDown
                        size={12}
                        className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180 text-blue-500' : 'text-zinc-400'}`}
                      />
                    )}
                    {/* Hover underline */}
                    {!isActive(link.path) && (
                      <span className="absolute bottom-1 left-3 right-3 h-[1px] bg-gradient-to-r from-blue-500 to-sky-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full" />
                    )}
                  </Link>

                  {/* ── SERVICES MEGA DROPDOWN ── */}
                  {link.hasDropdown && (
                    <AnimatePresence>
                      {isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          onMouseEnter={handleServicesEnter}
                          onMouseLeave={handleServicesLeave}
                          className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[540px] bg-white border border-black/8 rounded-2xl shadow-2xl shadow-black/20 overflow-hidden z-50"
                        >
                          {/* Dropdown header */}
                          <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between bg-zinc-50">
                            <div className="flex items-center gap-2">
                              <Zap size={13} className="text-blue-500" />
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">Our Services</span>
                            </div>
                            <Link
                              to="/services"
                              className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-600 transition-colors"
                              onClick={() => setIsServicesOpen(false)}
                            >
                              View All <ArrowRight size={10} />
                            </Link>
                          </div>

                          {/* Services grid */}
                          <div className="p-3 grid grid-cols-2 gap-1">
                            {services.map((service) => (
                              <Link
                                key={service.name}
                                to={service.path}
                                onClick={() => setIsServicesOpen(false)}
                                className="flex items-center gap-3 p-3 rounded-xl hover:bg-zinc-50 transition-all group/item border border-transparent hover:border-zinc-100"
                              >
                                <div className={`w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0 group-hover/item:bg-zinc-200 transition-all ${service.color}`}>
                                  <service.icon size={17} />
                                </div>
                                <div className="min-w-0">
                                  <h4 className="text-[12px] font-bold text-zinc-800 group-hover/item:text-blue-500 transition-colors truncate">{service.name}</h4>
                                  <p className="text-[10px] text-zinc-400 truncate mt-0.5">{service.desc}</p>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {/* Dropdown footer CTA */}
                          <div className="px-4 py-3 border-t border-black/5 bg-zinc-50">
                            <Link
                              to="/contact"
                              onClick={() => setIsServicesOpen(false)}
                              className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-blue-700 to-sky-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:opacity-90 transition-all"
                            >
                              Get a Free Quote <ArrowRight size={12} />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* ── DESKTOP RIGHT ACTIONS ── */}
            <div className="hidden md:flex items-center gap-3 lg:gap-5">
              {/* Search */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`p-1.5 rounded-lg transition-all ${isSearchOpen
                  ? 'bg-blue-50 text-blue-500 border border-blue-200'
                  : 'text-zinc-500 hover:text-blue-500 hover:bg-blue-50'}`}
              >
                <Search size={15} />
              </button>

              <div className="w-px h-5 bg-black/10" />

              {user ? (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-700 to-sky-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-blue-500/20 hover:opacity-90 hover:scale-105 active:scale-95 transition-all"
                >
                  <LayoutDashboard size={13} /> Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-zinc-600 hover:text-blue-500 transition-colors px-1.5 py-1.5"
                  >
                    <LogIn size={13} /> Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-700 to-sky-500 text-white text-[9px] font-black uppercase tracking-widest rounded-lg shadow-lg shadow-blue-500/20 hover:opacity-90 hover:scale-105 active:scale-95 transition-all"
                  >
                    Join Now <UserPlus size={12} />
                  </Link>
                </>
              )}
            </div>

            {/* ── MOBILE ACTIONS (Hidden on Desktop) ── */}
            <div className="flex md:hidden items-center gap-1.5 md:gap-3">
              {/* Search Toggle */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-1.5 md:p-2.5 rounded-xl transition-all text-zinc-600 hover:text-blue-500 hover:bg-blue-50"
              >
                {isSearchOpen ? <X size={18} /> : <Search size={18} />}
              </button>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-1.5 md:p-2 rounded-xl transition-all text-zinc-700 hover:bg-zinc-100"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={isOpen ? 'close' : 'open'}
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {isOpen ? <X size={20} className="md:size-6" /> : <Menu size={20} className="md:size-6" />}
                  </motion.div>
                </AnimatePresence>
              </button>
            </div>
          </div>

          {/* ── SEARCH BAR EXPANSION ── */}
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-t border-slate-100"
              >
                <div className="px-4 md:px-6 py-3">
                  <div className="relative flex items-center">
                    <Search className="absolute left-4 text-slate-400" size={16} />
                    <input
                      type="text"
                      placeholder="Search services, products..."
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500/40 focus:bg-white transition-all shadow-inner"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="absolute right-4 text-slate-400 hover:text-slate-600">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── MOBILE MENU ── */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="md:hidden overflow-hidden border-t border-slate-100"
              >
                <div className="px-4 py-4 flex flex-col gap-1">
                  {navLinks.map((link, idx) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex flex-col"
                    >
                      <div
                        className={`flex items-center justify-between px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-[10px] cursor-pointer transition-all ${isActive(link.path)
                          ? 'bg-blue-50 text-blue-600 border border-blue-100'
                          : 'text-slate-500 hover:text-blue-600 hover:bg-slate-50'
                          }`}
                        onClick={() => {
                          if (link.hasDropdown) {
                            setIsMobileServicesOpen(!isMobileServicesOpen);
                          } else {
                            setIsOpen(false);
                          }
                        }}
                      >
                        <Link
                          to={link.hasDropdown ? '#' : link.path}
                          className="flex items-center gap-2 flex-1"
                          onClick={(e) => link.hasDropdown && e.preventDefault()}
                        >
                          {isActive(link.path) && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                          {link.name}
                        </Link>
                        {link.hasDropdown && (
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180 text-blue-500' : ''}`}
                          />
                        )}
                      </div>

                      {/* Mobile Services Submenu */}
                      {link.hasDropdown && (
                        <AnimatePresence>
                          {isMobileServicesOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-1 ml-2 bg-slate-50 rounded-xl overflow-hidden border border-slate-100"
                            >
                              {services.map((service) => (
                                <Link
                                  key={service.name}
                                  to={service.path}
                                  className={`flex items-center gap-3 px-4 py-2.5 text-[10px] font-bold text-slate-500 hover:text-blue-600 hover:bg-slate-100 transition-all border-b border-slate-100 last:border-0 ${service.color}`}
                                  onClick={() => { setIsOpen(false); setIsMobileServicesOpen(false); }}
                                >
                                  <service.icon size={13} />
                                  <span className="text-slate-600 uppercase tracking-wide">{service.name}</span>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </motion.div>
                  ))}

                  {/* Mobile Auth Buttons */}
                  <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-slate-100">
                    {user ? (
                      <Link
                        to="/dashboard"
                        className="w-full py-3.5 bg-gradient-to-r from-blue-700 to-sky-500 text-white text-center font-black uppercase tracking-widest text-[11px] rounded-xl shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                        onClick={() => setIsOpen(false)}
                      >
                        <LayoutDashboard size={15} /> Dashboard
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="w-full py-3 bg-slate-100 text-slate-900 text-center font-black uppercase tracking-widest text-[10px] rounded-xl flex items-center justify-center gap-2 hover:bg-slate-200 transition-all border border-slate-200"
                          onClick={() => setIsOpen(false)}
                        >
                          <LogIn size={13} /> Login
                        </Link>
                        <Link
                          to="/register"
                          className="w-full py-3 bg-gradient-to-r from-blue-700 to-sky-500 text-white text-center font-black uppercase tracking-widest text-[10px] rounded-xl shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 active:scale-95 transition-all"
                          onClick={() => setIsOpen(false)}
                        >
                          <UserPlus size={13} /> Join Now
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </nav>
      </div>
    </header>
  );
};

export default Header;
